import { type Dispatch, type SetStateAction, useEffect } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { GenericForm } from 'src/components';
import { ConfigTabs } from 'src/pages/Settings/memberships/components/config-tabs';
import type { PlansTabFormValues } from 'src/pages/Settings/memberships/components/plans-tab/types';

export type MembershipsConfigViewFormProps = {
  data: PlansTabFormValues;
  readOnly: boolean;
  onSaveRequestChange: Dispatch<
    SetStateAction<
      ((
        ) => Promise<{ values: PlansTabFormValues; reset: () => void } | null>) | null
    >
  >;
  onDirtyChange: (isDirty: boolean) => void;
  onSubmitStateChange: (state: {
    isValid: boolean;
    isValidating: boolean;
    isSubmitting: boolean;
  }) => void;
  /** Called with current form values on every change so the preview can update live. */
  onFormValuesChange?: (values: PlansTabFormValues) => void;
};

export const MembershipsConfigViewForm = ({
  data,
  readOnly,
  onSaveRequestChange,
  onDirtyChange,
  onSubmitStateChange,
  onFormValuesChange,
}: MembershipsConfigViewFormProps) => {
  const formMethods = useForm<PlansTabFormValues>({
    defaultValues: data,
    mode: 'onChange',
  });
  const { isDirty, isValid, isValidating, isSubmitting } = useFormState({
    control: formMethods.control,
  });

  // Report current form values so the preview can update immediately on every change.
  useEffect(() => {
    onFormValuesChange?.(formMethods.getValues());
    const { unsubscribe } = formMethods.watch((values) => {
      onFormValuesChange?.(values as PlansTabFormValues);
    });
    return () => {
      unsubscribe();
    };
  }, [formMethods, onFormValuesChange]);

  useEffect(() => {
    return () => {
      onSaveRequestChange(null);
      onDirtyChange(false);
      onSubmitStateChange({ isValid: false, isValidating: false, isSubmitting: false });
    };
  }, [onSaveRequestChange, onDirtyChange, onSubmitStateChange]);

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    onSubmitStateChange({ isValid, isValidating, isSubmitting });
  }, [isSubmitting, isValid, isValidating, onSubmitStateChange]);

  useEffect(() => {
    onSaveRequestChange(() => async () => {
      const ok = await formMethods.trigger();
      if (!ok) {
        return null;
      }

      const values = formMethods.getValues();

      return {
        values,
        reset: () => formMethods.reset(values),
      };
    });
  }, [formMethods, onSaveRequestChange]);

  return (
    <GenericForm<PlansTabFormValues> formMethods={formMethods} gap={3}>
      <ConfigTabs readOnly={readOnly} />
    </GenericForm>
  );
};
